import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useTheme } from "@/lib/theme";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";
import PdfCanvas from "@/components/PdfCanvas";
import VideoCanvas from "@/components/VideoCanvas";
import WebsiteCanvas from "@/components/WebsiteCanvas";
import { warnTouchPrecision } from "@/lib/touchWarn";
import { useDragResize } from "@/lib/useDragResize";
import { Lock, ShieldCheck, ThumbsUp, MessageSquare, Pin, Square, Send, GripVertical } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export default function ReviewLink() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const inviteId = searchParams.get("i");
  const { branding } = useTheme();
  const [meta, setMeta] = useState(null);
  const [wsBrand, setWsBrand] = useState(null);
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [payload, setPayload] = useState(null);
  const [reviewer, setReviewer] = useState({ name: "", email: "" });
  const [identified, setIdentified] = useState(false);

  // annotation
  const [tool, setTool] = useState("pin");
  const [pendingAnno, setPendingAnno] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [draftBody, setDraftBody] = useState("");
  const [websiteFullscreen, setWebsiteFullscreen] = useState(false);
  const [rectStart, setRectStart] = useState(null);
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyBody, setReplyBody] = useState("");

  // Draggable + resizable floating panel for website fullscreen
  const panel = useDragResize({
    initial: { x: Math.max(40, (typeof window !== "undefined" ? window.innerWidth : 1280) - 420), y: 80, w: 380, h: 560 },
    minW: 300, minH: 280,
    storageKey: "co_review_link_panel",
  });

  // approval
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveDecision, setApproveDecision] = useState("approved");
  const [approveComment, setApproveComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [approved, setApproved] = useState(null);

  const canvasRef = useRef(null);

  useEffect(() => {
    api.get(`/r/${token}`).then((r) => {
      setMeta(r.data);
      if (!r.data.requires_password) {
        loadPayload("");
        setUnlocked(true);
      }
    }).catch(() => toast.error("Link not found or expired"));
    // Fetch white-label branding (no-auth)
    api.get(`/r/${token}/branding`).then((r) => setWsBrand(r.data)).catch(() => {});
    // Track open analytics (fire-and-forget)
    api.post(`/r/${token}/track-open`).catch(() => {});
    // Personalized invite — pre-fill reviewer name/email
    if (inviteId) {
      api.get(`/r/${token}/invite/${inviteId}`).then((r) => {
        if (r.data?.name && r.data?.email) {
          setReviewer({ name: r.data.name, email: r.data.email });
          setIdentified(true);
        }
      }).catch(() => { /* invalid invite — let user identify manually */ });
      // Track visit
      api.post(`/r/${token}/track`, { invite_id: inviteId }).catch(() => {});
    }
  }, [token, inviteId]);

  // Apply white-label primary color CSS variable on this page
  useEffect(() => {
    if (wsBrand?.white_label && wsBrand?.primary_hsl) {
      const root = document.documentElement;
      const prev = root.style.getPropertyValue("--primary");
      root.style.setProperty("--primary", wsBrand.primary_hsl);
      return () => { root.style.setProperty("--primary", prev); };
    }
  }, [wsBrand]);

  const displayName = wsBrand?.white_label
    ? (wsBrand.workspace_name || branding.platform_name)
    : branding.platform_name;
  const displayLogo = wsBrand?.white_label ? wsBrand.logo_url : null;
  const accentText = wsBrand?.white_label ? wsBrand.accent_text : null;

  const loadPayload = async (pw) => {
    try {
      const { data } = await api.post(`/r/${token}/load`, { password: pw });
      setPayload(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Wrong password"));
      throw e;
    }
  };

  const unlock = async (e) => {
    e.preventDefault();
    try {
      await loadPayload(password);
      setUnlocked(true);
    } catch {}
  };

  const refreshComments = async () => {
    const { data } = await api.post(`/r/${token}/load`, { password });
    setPayload(data);
  };

  const onCanvasClick = (e) => {
    if (payload?.mode === "view") return;
    if (drafting || pendingAnno) return;
    if (tool !== "pin") return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPendingAnno({ type: "pin", x, y });
    setDrafting(true);
    warnTouchPrecision();
  };

  const onMouseDown = (e) => {
    if (payload?.mode === "view") return;
    if (tool !== "rect" || drafting) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setRectStart({ x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 });
  };
  const onMouseMove = (e) => {
    if (!rectStart) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPendingAnno({
      type: "rect",
      x: Math.min(rectStart.x, x), y: Math.min(rectStart.y, y),
      w: Math.abs(x - rectStart.x), h: Math.abs(y - rectStart.y),
    });
  };
  const onMouseUp = () => {
    if (rectStart && pendingAnno && (pendingAnno.w > 1 || pendingAnno.h > 1)) setDrafting(true);
    else setPendingAnno(null);
    setRectStart(null);
  };

  const submitComment = async () => {
    if (!draftBody.trim()) return;
    if (!identified) return toast.error("Tell us your name and email first");
    // Optimistic
    const tempId = `tmp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      body: draftBody,
      annotation: pendingAnno,
      is_internal: false,
      is_client: true,
      author_name: reviewer.name || "Reviewer",
      status: "open",
      created_at: new Date().toISOString(),
      parent_id: null,
      _pending: true,
    };
    setPayload((p) => p ? { ...p, comments: [...p.comments, optimistic] } : p);
    const body = draftBody, anno = pendingAnno;
    setDraftBody(""); setPendingAnno(null); setDrafting(false);
    try {
      await api.post(`/r/${token}/comments`, {
        body, annotation: anno, author_name: reviewer.name, author_email: reviewer.email,
      });
      refreshComments();
      toast.success("Comment sent");
    } catch (e) {
      setPayload((p) => p ? { ...p, comments: p.comments.filter((c) => c.id !== tempId) } : p);
      toast.error(apiErrorMessage(e, "Failed to send comment"));
    }
  };

  const submitReply = async (parentId) => {
    if (!replyBody.trim()) return toast.error("Type something first");
    if (!identified) return toast.error("Tell us your name and email first");
    const tempId = `tmp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      body: replyBody,
      annotation: null,
      is_internal: false,
      is_client: true,
      author_name: reviewer.name || "Reviewer",
      status: "open",
      created_at: new Date().toISOString(),
      parent_id: parentId,
      _pending: true,
    };
    setPayload((p) => p ? { ...p, comments: [...p.comments, optimistic] } : p);
    const body = replyBody;
    setReplyBody(""); setReplyingTo(null);
    try {
      await api.post(`/r/${token}/comments`, {
        body, parent_id: parentId, author_name: reviewer.name, author_email: reviewer.email,
      });
      refreshComments();
    } catch (e) {
      setPayload((p) => p ? { ...p, comments: p.comments.filter((c) => c.id !== tempId) } : p);
      toast.error(apiErrorMessage(e, "Failed to send reply"));
    }
  };

  const submitApproval = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/r/${token}/approve`, {
        decision: approveDecision,
        reviewer_name: reviewer.name || "Anonymous Reviewer",
        reviewer_email: reviewer.email || "anonymous@example.com",
        comment: approveComment,
      });
      setApproved(data);
      setApproveOpen(false);
      refreshComments();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to submit approval"));
    } finally { setBusy(false); }
  };

  // --- Render gates ---
  if (!meta) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  }

  if (meta.requires_password && !unlocked) {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6" data-theme="canvas">
        <form onSubmit={unlock} className="w-full max-w-sm border border-border p-8 bg-card">
          <Lock className="w-8 h-8 text-primary mb-4" />
          <div className="label-eyebrow mb-2">Protected review</div>
          <h1 className="heading-display text-2xl mb-2">{meta.item_title}</h1>
          <p className="text-sm text-muted-foreground mb-6">Shared by {meta.workspace_name}</p>
          <Label className="label-eyebrow mb-2 inline-block">Password</Label>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 mb-4" data-testid="link-password-input" />
          <Button type="submit" className="w-full rounded-none" data-testid="link-unlock-btn">Unlock</Button>
        </form>
      </div>
    );
  }

  if (!payload) return null;
  const { item, version, comments, mode } = payload;
  const isViewOnly = mode === "view";
  const isApprovedAlready = item.approval_status === "approved";
  const topLevelComments = comments.filter((c) => !c.parent_id);
  const childrenOf = (id) => comments.filter((c) => c.parent_id === id);

  return (
    <div className="min-h-screen flex flex-col bg-background" data-theme="canvas">
      {/* Top bar */}
      <header className="flex items-center justify-between px-4 py-2 border-b border-border bg-card">
        <div className="flex items-center gap-3 min-w-0">
          {displayLogo ? (
            <img src={displayLogo} alt={displayName} className="w-8 h-8 object-contain" data-testid="public-brand-logo" />
          ) : (
            <div className="w-8 h-8 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-black">
              {(displayName || "C").slice(0, 1)}
            </div>
          )}
          <div className="min-w-0">
            <div className="label-eyebrow text-[10px]">{meta.workspace_name} · v{version.version_number}</div>
            <div className="font-bold text-sm truncate" style={{ fontFamily: "var(--font-heading)" }} data-testid="public-brand-name">{item.title}</div>
          </div>
          <span className={
            item.approval_status === "approved" ? "chip chip-approved" :
            item.approval_status === "changes_requested" ? "chip chip-changes" :
            "chip chip-pending"
          }>
            {item.approval_status === "changes_requested" ? "changes" : item.approval_status}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {mode === "approve" && !isApprovedAlready && (
            <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-none" data-testid="approve-cta-btn">
                  <ShieldCheck className="w-4 h-4 mr-1.5" /> Approve / Request changes
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Decision on v{version.version_number}</DialogTitle>
                  <DialogDescription>Your decision will be recorded with an immutable certificate.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  {!identified && <IdentifyForm reviewer={reviewer} setReviewer={setReviewer} setIdentified={setIdentified} />}
                  <div>
                    <Label className="label-eyebrow mb-2 inline-block">Decision</Label>
                    <Select value={approveDecision} onValueChange={setApproveDecision}>
                      <SelectTrigger className="h-10" data-testid="approve-decision-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="approved">Approve this version</SelectItem>
                        <SelectItem value="changes_requested">Request changes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-eyebrow mb-2 inline-block">Optional note</Label>
                    <Textarea rows={3} value={approveComment} onChange={(e) => setApproveComment(e.target.value)} data-testid="approve-comment-input" />
                  </div>
                  <Button onClick={submitApproval} disabled={busy || !identified} className="w-full rounded-none" data-testid="approve-submit-btn">
                    {busy ? "Saving…" : approveDecision === "approved" ? "Approve" : "Request changes"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </header>

      {/* Approved banner */}
      {approved && (
        <div className="bg-foreground text-background px-4 py-3 text-sm flex items-center gap-3" data-testid="approval-receipt">
          <ThumbsUp className="w-4 h-4" />
          <span>{approved.decision === "approved" ? "Approved" : "Changes requested"} · Certificate {approved.certificate_no}</span>
        </div>
      )}

      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
        {/* Toolbar */}
        {!isViewOnly && (
          <div className="md:w-12 md:border-r border-b md:border-b-0 border-border bg-card flex md:flex-col items-center md:py-3 px-2 md:px-0 gap-2">
            <button onClick={() => setTool("pin")} className={`w-10 h-10 md:w-9 md:h-9 flex items-center justify-center rounded-sm ${tool === "pin" ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground"}`} data-testid="public-tool-pin"><Pin className="w-4 h-4" /></button>
            <button onClick={() => setTool("rect")} className={`w-10 h-10 md:w-9 md:h-9 flex items-center justify-center rounded-sm ${tool === "rect" ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground"}`} data-testid="public-tool-rect"><Square className="w-4 h-4" /></button>
          </div>
        )}

        <div className="flex-1 overflow-auto bg-muted/40 flex items-start justify-center p-3 md:p-6 min-h-[50vh] md:min-h-0">
          {item.item_type === "pdf" && version?.file_data_url ? (
            <PdfCanvas
              fileUrl={version.file_data_url}
              comments={comments}
              readOnly={isViewOnly}
              activeTool={tool}
              onCreateAnnotation={(anno) => { setPendingAnno(anno); setDrafting(true); warnTouchPrecision(); }}
              selectedCommentId={selectedCommentId}
              setSelectedCommentId={setSelectedCommentId}
              pendingAnno={pendingAnno}
              testidPrefix="public-anno"
            />
          ) : item.item_type === "video" && version?.file_data_url ? (
            <VideoCanvas
              fileUrl={version.file_data_url}
              comments={comments}
              readOnly={isViewOnly}
              activeTool={tool}
              onCreateAnnotation={(anno) => { setPendingAnno(anno); setDrafting(true); warnTouchPrecision(); }}
              selectedCommentId={selectedCommentId}
              setSelectedCommentId={setSelectedCommentId}
              pendingAnno={pendingAnno}
              testidPrefix="public-anno"
              reviewItemId={item.id}
            />
          ) : item.item_type === "website" ? (
            <WebsiteCanvas
              url={version?.file_data_url || ""}
              comments={comments}
              readOnly={isViewOnly}
              activeTool={tool}
              onCreateAnnotation={(anno) => { setPendingAnno(anno); setDrafting(true); warnTouchPrecision(); }}
              selectedCommentId={selectedCommentId}
              setSelectedCommentId={setSelectedCommentId}
              pendingAnno={pendingAnno}
              testidPrefix="public-anno-website"
              publicToken={token}
              fullscreen={websiteFullscreen}
              onToggleFullscreen={() => setWebsiteFullscreen((f) => !f)}
            />
          ) : (
          <div
            ref={canvasRef}
            className={`relative max-w-full ${isViewOnly ? "" : "cursor-crosshair"} select-none`}
            style={{ width: "min(100%, 1000px)" }}
            onClick={onCanvasClick}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            data-testid="public-annotation-canvas"
          >
            {item.item_type === "image" && version?.file_data_url && (
              <img src={version.file_data_url} className="w-full h-auto block pointer-events-none" alt={item.title} draggable={false} />
            )}

            {comments.map((c, i) => {
              if (!c.annotation) return null;
              if (c.annotation.type === "pin") {
                return (
                  <div key={c.id} className={`anno-pin ${c.status === "resolved" ? "resolved" : ""}`}
                    style={{ left: `${c.annotation.x}%`, top: `${c.annotation.y}%` }} data-testid={`public-anno-pin-${c.id}`}>
                    {i + 1}
                  </div>
                );
              }
              if (c.annotation.type === "rect") {
                return (
                  <div key={c.id} className="absolute border-2 border-primary bg-primary/10 pointer-events-none"
                    style={{ left: `${c.annotation.x}%`, top: `${c.annotation.y}%`, width: `${c.annotation.w}%`, height: `${c.annotation.h}%` }} />
                );
              }
              return null;
            })}
            {pendingAnno?.type === "pin" && (
              <div className="anno-pin pending" style={{ left: `${pendingAnno.x}%`, top: `${pendingAnno.y}%` }} data-testid="public-anno-pin-pending">+</div>
            )}
            {pendingAnno?.type === "rect" && (
              <div className="anno-rect pending absolute pointer-events-none"
                style={{ left: `${pendingAnno.x}%`, top: `${pendingAnno.y}%`, width: `${pendingAnno.w}%`, height: `${pendingAnno.h}%` }} data-testid="public-anno-rect-pending" />
            )}
          </div>
          )}
        </div>

        <aside
          className={
            websiteFullscreen
              ? "fixed z-50 bg-card border border-border shadow-2xl flex flex-col rounded-sm"
              : "w-full md:w-96 border-t md:border-t-0 md:border-l border-border bg-card flex flex-col"
          }
          style={websiteFullscreen ? { left: panel.x, top: panel.y, width: panel.w, height: panel.h } : undefined}
          data-testid={websiteFullscreen ? "public-comments-panel-floating" : "public-comments-panel"}
        >
          <div
            className="px-4 py-3 border-b border-border flex items-center gap-2"
            onMouseDown={websiteFullscreen ? panel.onDragMouseDown : undefined}
            style={websiteFullscreen ? { cursor: "move", userSelect: "none" } : undefined}
            data-testid="public-comments-panel-header"
          >
            {websiteFullscreen && <GripVertical className="w-3.5 h-3.5 text-muted-foreground" />}
            <div className="label-eyebrow">Feedback · {topLevelComments.length}</div>
          </div>
          {!identified && !isViewOnly && (
            <div className="p-4 border-b border-border bg-muted/40">
              <IdentifyForm reviewer={reviewer} setReviewer={setReviewer} setIdentified={setIdentified} />
            </div>
          )}
          <div className="flex-1 overflow-auto">
            {drafting && (
              <div className="p-4 border-b border-border bg-muted/40">
                <div className="label-eyebrow mb-2">New {pendingAnno?.type} comment</div>
                <Textarea rows={3} value={draftBody} onChange={(e) => setDraftBody(e.target.value)} placeholder="Your feedback…" className="mb-2" autoFocus data-testid="public-draft-textarea" />
                <div className="flex gap-2">
                  <Button size="sm" onClick={submitComment} className="rounded-none flex-1" disabled={!identified} data-testid="public-draft-submit"><Send className="w-3.5 h-3.5 mr-1.5" />Send</Button>
                  <Button size="sm" variant="outline" onClick={() => { setDrafting(false); setPendingAnno(null); }} className="rounded-none" data-testid="public-draft-cancel">Cancel</Button>
                </div>
              </div>
            )}
            {topLevelComments.length === 0 && !drafting && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                {isViewOnly ? "View-only link." : `Click the ${item.item_type} to drop a pin or drag to draw an area.`}
              </div>
            )}
            <ul>
              {item.item_type === "website" ? (
                groupByUrl(topLevelComments).map(([groupUrl, list]) => (
                  <li key={groupUrl || "no-url"} data-testid={`public-comment-group-${groupUrl || "default"}`}>
                    <div className="px-4 py-2 bg-muted/40 border-b border-border text-[10px] uppercase tracking-widest font-mono flex items-center gap-2">
                      <span className="text-muted-foreground">{groupUrl ? shortUrl(groupUrl) : "Unanchored"}</span>
                      <span className="chip text-[9px] ml-auto">{list.length}</span>
                    </div>
                    <ul>
                      {list.map((c) => (
                        <PublicCommentNode
                          key={c.id}
                          comment={c}
                          index={comments.indexOf(c) + 1}
                          childrenList={childrenOf(c.id)}
                          selectedCommentId={selectedCommentId}
                          setSelectedCommentId={setSelectedCommentId}
                          replyingTo={replyingTo}
                          setReplyingTo={setReplyingTo}
                          replyBody={replyBody}
                          setReplyBody={setReplyBody}
                          onSubmitReply={submitReply}
                          identified={identified}
                          isViewOnly={isViewOnly}
                        />
                      ))}
                    </ul>
                  </li>
                ))
              ) : (
                topLevelComments.map((c) => (
                  <PublicCommentNode
                    key={c.id}
                    comment={c}
                    index={comments.indexOf(c) + 1}
                    childrenList={childrenOf(c.id)}
                    selectedCommentId={selectedCommentId}
                    setSelectedCommentId={setSelectedCommentId}
                    replyingTo={replyingTo}
                    setReplyingTo={setReplyingTo}
                    replyBody={replyBody}
                    setReplyBody={setReplyBody}
                    onSubmitReply={submitReply}
                    identified={identified}
                    isViewOnly={isViewOnly}
                  />
                ))
              )}
            </ul>
          </div>
          {websiteFullscreen && (
            <div
              className="absolute right-0 bottom-0 w-4 h-4 cursor-se-resize"
              onMouseDown={panel.onResizeMouseDown}
              style={{
                background: "linear-gradient(135deg, transparent 0%, transparent 50%, hsl(var(--muted-foreground) / 0.4) 50%, hsl(var(--muted-foreground) / 0.4) 100%)",
              }}
              data-testid="public-panel-resize-handle"
              title="Resize"
            />
          )}
        </aside>
      </div>
      {accentText && (
        <div className="px-4 py-2 text-center text-xs text-muted-foreground border-t border-border bg-card" data-testid="public-accent-text">
          {accentText}
        </div>
      )}
    </div>
  );
}

function IdentifyForm({ reviewer, setReviewer, setIdentified }) {
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((reviewer.email || "").trim());
  return (
    <div className="space-y-2">
      <div className="label-eyebrow">Who&apos;s reviewing?</div>
      <Input placeholder="Your name" value={reviewer.name} onChange={(e) => setReviewer({ ...reviewer, name: e.target.value })} className="h-9" data-testid="reviewer-name-input" />
      <Input type="email" placeholder="Your email" value={reviewer.email} onChange={(e) => setReviewer({ ...reviewer, email: e.target.value })} className="h-9" data-testid="reviewer-email-input" />
      <Button size="sm" className="w-full rounded-none" onClick={() => {
        if (!reviewer.name?.trim()) return toast.error("Please enter your name");
        if (!isValidEmail) return toast.error("Please enter a valid email address");
        setIdentified(true);
        toast.success("Welcome, " + reviewer.name);
      }} data-testid="reviewer-identify-btn">
        Continue
      </Button>
    </div>
  );
}

function shortUrl(u) {
  if (!u || typeof u !== "string") return "";
  try {
    const url = new URL(u);
    const path = url.pathname === "/" ? "" : url.pathname;
    return `${url.hostname}${path}`;
  } catch { return u.length > 28 ? u.slice(0, 28) + "…" : u; }
}

function groupByUrl(commentsList) {
  const map = new Map();
  for (const c of commentsList) {
    const k = c.annotation?.url || "";
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(c);
  }
  return Array.from(map.entries());
}

function fmtVideoTime(s) {
  if (typeof s !== "number" || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
}

function PublicCommentNode({
  comment: c, index, childrenList, selectedCommentId, setSelectedCommentId,
  replyingTo, setReplyingTo, replyBody, setReplyBody, onSubmitReply,
  identified, isViewOnly,
}) {
  const isReplying = replyingTo === c.id;
  return (
    <li
      onClick={() => setSelectedCommentId(c.id)}
      className={`px-4 py-3 border-b border-border cursor-pointer hover:bg-muted/40 ${selectedCommentId === c.id ? "bg-muted/40" : ""} ${c._pending ? "opacity-60" : ""}`}
      data-testid={`public-comment-${c.id}`}
    >
      <div className="flex items-center justify-between text-xs mb-1">
        <div className="flex items-center gap-2 flex-wrap">
          {c.annotation && (
            <span className="anno-pin relative w-5 h-5 text-[10px]" style={{ position: "relative", transform: "none", border: "none", boxShadow: "none" }}>{index}</span>
          )}
          <span className="font-medium">{c.author_name}</span>
          {c.annotation?.t != null && (
            <span className="chip text-[9px] font-mono">@ {fmtVideoTime(c.annotation.t)}</span>
          )}
          {c.annotation?.page != null && (
            <span className="chip text-[9px]">p.{c.annotation.page}</span>
          )}
          {c.annotation?.url && (
            <span className="chip text-[9px] font-mono" title={c.annotation.url} data-testid={`public-comment-url-${c.id}`}>
              {shortUrl(c.annotation.url)}
            </span>
          )}
          {c.is_client && <span className="chip chip-client text-[9px]">client</span>}
        </div>
        <span className={c.status === "resolved" ? "chip chip-resolved" : "chip chip-open"}>{c.status}</span>
      </div>
      <div className="text-sm mb-1 whitespace-pre-wrap">{c.body}</div>
      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
        <span>{fmtRelative(c.created_at)}</span>
        {!isViewOnly && !c._pending && (
          <button
            onClick={(e) => { e.stopPropagation(); setReplyingTo(isReplying ? null : c.id); }}
            className="hover:text-primary inline-flex items-center"
            data-testid={`public-comment-reply-${c.id}`}
          >
            <MessageSquare className="w-3 h-3 mr-1" /> Reply
          </button>
        )}
      </div>

      {isReplying && (
        <div className="mt-3 p-3 bg-muted/40 border border-border rounded-sm" onClick={(e) => e.stopPropagation()} data-testid={`public-reply-composer-${c.id}`}>
          <Textarea
            rows={2}
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            placeholder="Reply…"
            className="mb-2"
            data-testid={`public-reply-textarea-${c.id}`}
            autoFocus
          />
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => { setReplyingTo(null); setReplyBody(""); }} className="rounded-none h-7 text-xs" data-testid={`public-reply-cancel-${c.id}`}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => onSubmitReply(c.id)} disabled={!identified} className="rounded-none h-7 text-xs" data-testid={`public-reply-submit-${c.id}`}>
              <Send className="w-3 h-3 mr-1" /> Reply
            </Button>
          </div>
        </div>
      )}

      {childrenList && childrenList.length > 0 && (
        <ul className="mt-2 pl-3 border-l-2 border-border" data-testid={`public-comment-children-${c.id}`}>
          {childrenList.map((ch) => (
            <PublicReplyItem key={ch.id} comment={ch} />
          ))}
        </ul>
      )}
    </li>
  );
}

function PublicReplyItem({ comment: c }) {
  return (
    <li
      className={`px-3 py-2 border-b border-border last:border-b-0 ${c._pending ? "opacity-60" : ""}`}
      data-testid={`public-comment-${c.id}`}
    >
      <div className="flex items-center gap-2 text-xs mb-1 flex-wrap">
        <span className="font-medium">{c.author_name}</span>
        {c.is_client && <span className="chip chip-client text-[9px]">client</span>}
        <span className="text-[10px] text-muted-foreground ml-auto">{fmtRelative(c.created_at)}</span>
      </div>
      <div className="text-sm whitespace-pre-wrap">{c.body}</div>
    </li>
  );
}
